import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, Observable } from 'rxjs';
import { TokenService } from './token.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HelperService } from './helper.service';
import { UiService } from './ui.service';
import { ANONYMIZED_VALUES, CANDIDATES_RIGHTS } from '../shared/constants';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {

  private userState = new BehaviorSubject<boolean>(false);
  private userName = new BehaviorSubject<string>(null);
  public hasHeader = new BehaviorSubject<boolean>(false);
  public userName$ = this.userName.asObservable();

  public hasRights = false;
  public sysMode = null;
  public sysType = null;
  public features: Array<string> = [];

  constructor(
    private token: TokenService,
    private router: Router,
    private helper: HelperService,
    private ui: UiService,
    private transloco: TranslocoService
  ) {
    this.setAuthState();
    this.hasRights = !this.hasNoRights();
  }

  public setAuthState(): void {
    this.userState.next(this.token.isLoggedIn());
    const payload = this.token.getPayload();
    if (payload !== null) {
      this.userName.next(payload.user?.firstname + ' ' + payload.user?.lastname);
      if (payload.user?.language === 'DE') {
        this.helper.setLanguage('de');
      } else {
        this.helper.setLanguage('en');
      }
    }
    this.hasHeader.next(!this.hasNoHeader());
  }

  public getRoles(): Array<string> {
    const payload = this.token.getPayload();
    return payload?.user?.roles || [];
  }

  public hasRole(role: string): boolean {
    const payload = this.token.getPayload();
    const roles: Array<string> = payload?.user?.roles || [];
    return roles.indexOf(role) !== -1;
  }

  public hasRight(right: string = null): boolean {
    if (this.isSuperUser() || right === null) {
      return true;
    }
    const payload = this.token.getPayload();
    const rights: Array<string> = payload?.user?.rights || null;
    return rights !== null && rights.indexOf(right) !== -1;
  }

  public hasOneRight(rights: Array<string>): boolean {
    if (this.isSuperUser()) {
      return true;
    }
    const payload = this.token.getPayload();
    const userRights: Array<string> = payload?.user?.rights || [];
    return rights.some(right => userRights.indexOf(right) !== -1);
  }

  public checkRightOrRedirect(right: string, route: string = null): boolean {
    if (this.hasRight(right)) {
      return true;
    } else {
      this.ui.toastError(this.transloco.translate('auth.no_permission'));
      this.router.navigate([route || 'dashboard']);
      return false;
    }
  }

  public hasNoRights(): boolean {
    if (this.isSuperUser()) {
      return false;
    }
    const payload = this.token.getPayload();
    const rights: Array<string> = payload?.user?.rights || [];
    return rights === null || rights?.length === 0;
  }

  public hasNoHeader(): boolean {
    if (this.isSuperUser()) {
      return false;
    }
    const payload = this.token.getPayload();
    const roles: Array<string> = payload?.user?.roles || [];
    return roles === null || roles?.length === 0 || (roles?.length === 1 && roles[0] === 'orderer');
  }

  public hasAvailabilities(): boolean {
    return this.hasRole('moderator') ||
      this.hasRole('moderator_external') ||
      this.hasRole('observer') ||
      this.hasRole('observer_gem');
  }

  public hasPrivacy(): boolean {
    const payload = this.token.getPayload();
    return payload.user?.privacy === true;
  }

  public isSuperUser(): boolean {
    const payload = this.token.getPayload();
    const roles: Array<string> = payload?.user?.roles || [];
    return roles.indexOf('superuser') !== -1;
  }

  public isModerator(): boolean {
    const payload = this.token.getPayload();
    const roles: Array<string> = payload?.user?.roles || [];
    return roles.indexOf('moderator') !== -1 || roles.indexOf('moderator_external') !== -1;
  }

  public isObserver(): boolean {
    const payload = this.token.getPayload();
    const roles: Array<string> = payload?.user?.roles || [];
    return roles.indexOf('observer') !== -1 || roles.indexOf('observer_gem') !== -1;
  }

  public login(token: string, refresh: string): void {
    this.token.setToken(token);
    this.token.setRefreshToken(refresh);
    this.setAuthState();
    this.hasRights = !this.hasNoRights();
    this.router.navigate([this.getRedirectRoute()]);
  }

  public refreshToken(token: string, refresh: string = null): void {
    this.token.setToken(token);
    if (refresh !== null) {
      this.token.setRefreshToken(refresh);
    }
    this.setAuthState();
  }

  public logout(): void {
    localStorage.removeItem('orders-list');
    localStorage.removeItem('clearings-list');
    this.token.removeToken();
    this.token.removeRefreshToken();
    this.setAuthState();
    this.router.navigate(['login']);
  }

  public setSysMode(mode: string): void {
    this.sysMode = mode;
  }

  public getSysMode(): string {
    return this.sysMode;
  }

  public setSysType(type: string): void {
    this.sysType = type;
  }

  public getSysType(): string {
    return this.sysType;
  }

  public setFeatures(features: Array<string>): void {
    this.features = features;
  }

  public getFeatures(): Array<string> {
    return this.features;
  }

  public hasFeature(feature: string): boolean {
    return this.features.indexOf(feature) !== -1;
  }
  public getAnonymizedValue(value: string): string {
    return this.hasOneRight(CANDIDATES_RIGHTS) ? value : ANONYMIZED_VALUES;
  }

  public canSeeFiles(): boolean {
    return this.hasRight(
      this.getSysMode() === 'planner' ? 'admin.planning-orders.show_files' : 'admin.ac.files'
    );
  }

  private getRedirectRoute(): string {
    if (this.sysMode === 'default') {
      // mode: default
      return 'acs/planned';
    } else if (this.sysMode === 'planner') {
      // mode: planner
      if (this.hasRight('admin.planning-orders.list-clearing')) {
        return 'planning/clearings';
      }
      if (this.isModerator()) {
        if (this.hasRight('admin.planning-orders.list')) {
          return 'planning/orders';
        } else {
          return 'my-availabilities';
        }
      }
      if (this.isObserver()) {
        return 'my-availabilities';
      }
      return 'planning/orderform';
    }
    return 'dashboard';
  }

}
